/**
 * Sidebar v2 search result row.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SidebarSearchResultRow, ~lines 1992-2142) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: content matches use Forge's orchestration
 * search RPC; file drops on rows are not wired in Forge.
 */
import { memo } from "react";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useGitStatus } from "~/lib/gitStatusState";
import { selectThreadTerminalState, useTerminalStateStore } from "~/terminalStateStore";
import {
  resolveDisplayedThreadBranch,
  terminalStatusFromRunningIds,
} from "~/components/sidebar/SidebarThreadRow.helpers";
import { useSidebarThreadPullRequest } from "~/components/sidebar/SidebarThreadPullRequestBadge";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipTrigger } from "~/components/ui/tooltip";

import { threadTimeLabel } from "./sidebarV2Chrome";
import { SidebarV2Tooltip } from "./sidebarV2Tooltip";
type EnvironmentMachineKind = string | null;
import type { SidebarV2Thread } from "./sidebarV2Threads";
import {
  SidebarV2BranchMeta,
  SidebarV2OriginIcon,
  SidebarV2ProjectIcon,
  SidebarV2ProviderIcon,
} from "./SidebarV2IdentityMeta";
import { useRetainedValue, useSidebarRowSubscriptionLease } from "./sidebarV2RowHooks";
import type { SidebarContentMatch } from "./useSidebarV2ContentSearch";
import { SidebarV2SearchExcerpt } from "./sidebarV2SearchExcerpt";

interface SidebarV2SearchResultRowProps {
  readonly thread: SidebarV2Thread;
  readonly projectDisplayName: string | null;
  readonly searchMatch: SidebarContentMatch | null;
  readonly environmentLabel: string | null;
  readonly environmentMachine: EnvironmentMachineKind | null;
  readonly threadOrigin?: import("~/components/sidebar/threadOrigin").ThreadOrigin;
  readonly projectCwd: string | null;
  readonly projectCanonicalIdentity:
    | import("~/components/ProjectIcon").ProjectCanonicalIdentity
    | undefined;
  readonly linkedPrUrl: string | null;
  readonly modelLabel: string | null;
  readonly isHighlighted: boolean;
  readonly isRouteActive: boolean;
  readonly resultId: string;
  readonly searchQuery: string;
  readonly onHighlight: () => void;
  readonly onSelect: () => void;
}

/** Render one keyboard-selectable thread result with its project identity and optional message hit. */
// fallow-ignore-next-line complexity -- row composes live git, terminal, PR, identity, and search metadata
export const SidebarV2SearchResultRow = memo(function SidebarV2SearchResultRow(
  props: SidebarV2SearchResultRowProps,
) {
  const { thread } = props;
  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const lease = useSidebarRowSubscriptionLease(props.isHighlighted || props.isRouteActive);
  const gitCwd = thread.worktreePath ?? props.projectCwd;
  const gitStatusState = useGitStatus({
    environmentId: lease.leaseLiveStatus ? thread.environmentId : null,
    cwd: lease.leaseLiveStatus ? gitCwd : null,
  });
  const gitStatus = useRetainedValue(`${thread.environmentId}:${gitCwd}`, gitStatusState.data);
  const displayBranch = resolveDisplayedThreadBranch({
    threadBranch: thread.branch,
    currentGitBranch: gitStatus?.branch ?? null,
  });
  const runningTerminalIds = useTerminalStateStore(
    (state) =>
      selectThreadTerminalState(state.terminalStateByThreadKey, threadRef).runningTerminalIds,
  );
  const terminalStatus = terminalStatusFromRunningIds(runningTerminalIds);
  const pullRequests = useSidebarThreadPullRequest(
    thread,
    displayBranch,
    gitStatus,
    props.linkedPrUrl,
  );
  return (
    <li role="presentation" className="list-none">
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              id={props.resultId}
              ref={lease.rowRef}
              type="button"
              role="option"
              // aria-activedescendant options: focus stays on the search input,
              // which owns all keyboard interaction for the listbox.
              tabIndex={-1}
              aria-selected={props.isHighlighted}
              aria-current={props.isRouteActive ? "page" : undefined}
              aria-label={
                props.projectDisplayName
                  ? `${thread.title}, ${props.projectDisplayName}`
                  : thread.title
              }
              onMouseMove={props.onHighlight}
              onClick={props.onSelect}
              className={cn(
                "flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1 text-left text-sm outline-none",
                props.isHighlighted || props.isRouteActive
                  ? "bg-sidebar-row-active text-sidebar-foreground"
                  : "text-sidebar-muted-foreground/75 hover:bg-sidebar-row-hover hover:text-sidebar-foreground",
              )}
            />
          }
        >
          <SidebarV2ProjectIcon
            environmentId={thread.environmentId}
            cwd={props.projectCwd}
            name={props.projectDisplayName}
            canonicalIdentity={props.projectCanonicalIdentity}
            className="size-4 shrink-0"
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex min-w-0 items-center gap-2.5">
              <span className="min-w-0 flex-1 truncate">{thread.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground/55 tabular-nums">
                {threadTimeLabel(thread)}
              </span>
            </span>
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/45">
              <SidebarV2BranchMeta branch={displayBranch} />
              <SidebarV2OriginIcon origin={props.threadOrigin ?? "current-device"} />
              <SidebarV2ProviderIcon
                provider={thread.modelSelection?.provider}
                className="size-3.5"
              />
              {(pullRequests.pr?.number ?? thread.linkedPullRequest?.number) != null ? (
                <span className="shrink-0">
                  #{pullRequests.pr?.number ?? thread.linkedPullRequest?.number}
                </span>
              ) : null}
            </span>
            {props.searchMatch ? (
              <SidebarV2SearchExcerpt match={props.searchMatch} query={props.searchQuery} />
            ) : null}
          </span>
        </TooltipTrigger>
        <SidebarV2Tooltip
          thread={thread}
          projectDisplayName={props.projectDisplayName}
          environmentLabel={props.environmentLabel}
          environmentMachine={props.environmentMachine}
          modelLabel={thread.modelSelection?.model ?? props.modelLabel}
          terminalStatus={terminalStatus}
          terminalProcessCount={runningTerminalIds.length}
          pullRequestEntries={pullRequests.entries}
          projectCwd={props.projectCwd}
          projectCanonicalIdentity={props.projectCanonicalIdentity}
          displayBranch={displayBranch}
          threadOrigin={props.threadOrigin ?? "current-device"}
        />
      </Tooltip>
    </li>
  );
});
